import React from 'react';
import { motion } from 'framer-motion';
import ParallaxTower from './tower/ParallaxTower';
import { ChevronLeft, ArrowRight } from 'lucide-react';

// ─── Animation Variants ───────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1], delay },
  }),
};

// ─── Data ─────────────────────────────────────────────────────────────────────

const WORK_TYPES = [
  {
    label: 'Quality Assurance Systems',
    descriptor:
      'Hierarchical QA gates, traveler tracking, multi-level sign-off workflows, and 50+ device concurrencies to support fleet companies.',
  },
  {
    label: 'CFO Tools',
    descriptor:
      'Tracking and Charting company data through disparate sources. We build software for the process already in place.',
  },
  {
    label: 'Lead Capture Systems',
    descriptor:
      'Publicly hosted forms with Google Sheets backend, for fast and free access to generated leads.',
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-medium tracking-[0.25em] text-white/30 uppercase mb-4">
    {children}
  </p>
);

const Divider = () => (
  <div className="w-full h-px bg-gradient-to-r from-cyan-400/20 via-white/10 to-transparent my-16" />
);

/**
 * Developer Plaque — "Minimal Pill" variant
 * Subtle attribution badge for Tower Integrations.
 * Designed to sit at the bottom of legal/info pages.
 */
const DevPlaque = () => {
  return (
    <div className="flex flex-col items-center gap-3 pt-10 pb-6 relative z-10">
      {/* Separator */}
      <div className="w-16 h-px bg-white/10" />

      {/* Pill Badge */}
      <div className="group inline-flex items-center gap-3 px-5 py-2.5 bg-white/[0.04] border border-white/10 rounded-full transition-all duration-300 ease-in-out hover:border-cyan-400/40 hover:bg-white/[0.07] hover:shadow-[0_4px_12px_-2px_rgba(34,211,238,0.15)]">
        {/* Icon — Layered diamond / tower motif */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-white/50 shrink-0 transition-colors duration-300 group-hover:text-cyan-400"
          aria-hidden="true"
        >
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <path d="M2 17l10 5 10-5" />
          <path d="M2 12l10 5 10-5" />
        </svg>

        {/* Attribution Text */}
        <span className="flex items-baseline gap-1.5 text-sm leading-none">
          <span className="text-white/40 font-normal">Engineered by</span>
          <a
            href="https://tower-integrations.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white font-semibold tracking-tight transition-colors duration-200 hover:text-cyan-400"
          >
            Tower
          </a>
        </span>
      </div>

      {/* Contact micro-links */}
      <div className="flex items-center gap-2 text-[11px] text-white/30 font-normal">
        <a
          href="https://tower-integrations.com"
          target="_blank"
          rel="noopener noreferrer"
          className="transition-colors duration-200 hover:text-white/60"
        >
          tower-integrations.com
        </a>
        <span className="select-none" aria-hidden="true">
          ·
        </span>
        <a
          href="mailto:dev.group@tower-integrations.com"
          className="transition-colors duration-200 hover:text-white/60"
        >
          dev.group@tower-integrations.com
        </a>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AboutPage = ({ onBack, onContact, onSeePortfolio }) => {
  return (
    <div className="relative min-h-screen bg-[#023666] text-white overflow-y-auto">

      {/* Background */}
      <ParallaxTower activeIndex={5} />

      {/* Scrim — dark overlay for readability */}
      <div className="fixed inset-0 bg-gradient-to-b from-[#01223f]/70 via-[#01223f]/50 to-[#01223f]/80 pointer-events-none z-0" />

      {/* Back Button */}
      <div className="relative z-20 px-6 pt-6">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white/90 transition-colors duration-200 group"
        >
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to Home
        </button>
      </div>

      {/* Page Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 pb-32 pt-12">

        {/* ── Section 1: Hero ─────────────────────────────────────────────── */}
        <motion.section
          className="mb-24"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
        >
          <motion.p
            variants={fadeUp}
            custom={0}
            className="text-[11px] font-medium tracking-[0.3em] text-cyan-400/70 uppercase mb-6"
          >
            Tower Integrations
          </motion.p>

          <motion.h1
            variants={fadeUp}
            custom={0.05}
            className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-6 text-transparent bg-clip-text bg-gradient-to-br from-white via-white/90 to-white/60"
          >
            Stop Renting Your Competitive Edge.
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={0.1}
            className="text-lg text-white/55 leading-relaxed max-w-2xl"
          >
            Your software should be as unique as your business. We build custom,
            purpose-driven solutions designed exclusively for your operations.
            No licensing fees, no feature bloat.
          </motion.p>
        </motion.section>

        <Divider />

        {/* ── Section 2: The Problem ───────────────────────────────────────── */}
        <motion.section
          className="mb-24"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
        >
          <motion.div variants={fadeUp} custom={0}>
            <SectionLabel>The Shift</SectionLabel>
          </motion.div>

          <motion.blockquote
            variants={fadeUp}
            custom={0.05}
            className="text-2xl sm:text-3xl font-semibold leading-snug text-white/90 border-l-2 border-cyan-400/50 pl-6 mb-8"
          >
            The software industry is undergoing a fundamental transformation.
          </motion.blockquote>

          <motion.div
            variants={fadeUp}
            custom={0.1}
            className="text-base text-white/65 leading-relaxed space-y-4 max-w-2xl"
          >
            <p>
              For too long, businesses have been forced into a compromise: renting generic, off-the-shelf software designed for the masses. You've had to bend your unique workflows to fit platforms built for thousands of other companies.
            </p>
            <p>
              Software companies relied on amortizing their costs across hundreds of thousands of users — meaning the software was never truly built with your specific needs in mind. That necessity is obsolete.
            </p>
          </motion.div>
        </motion.section>

        <Divider />

        {/* ── Section 3: Origin & Credibility ─────────────────────────────── */}
        <motion.section
          className="mb-24"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
        >
          <motion.div variants={fadeUp} custom={0}>
            <SectionLabel>Our Vision</SectionLabel>
          </motion.div>

          <motion.h2
            variants={fadeUp}
            custom={0.05}
            className="text-3xl font-bold leading-tight mb-8 text-white"
          >
            The Future of Digital Ownership.
          </motion.h2>

          <motion.div
            variants={fadeUp}
            custom={0.1}
            className="text-base text-white/65 leading-relaxed space-y-4 max-w-2xl"
          >
            <p>
              We are championing the shift from software tenancy to software ownership. We believe that the businesses of tomorrow will be powered by proprietary technology.
            </p>
            <p>
              We engineer this reality across industries:
            </p>
            <ul className="space-y-3 my-6">
              <li className="flex items-start gap-3">
                <ArrowRight className="w-5 h-5 text-cyan-400/80 shrink-0 mt-0.5" />
                <span>Financial models for investment banks</span>
              </li>
              <li className="flex items-start gap-3">
                <ArrowRight className="w-5 h-5 text-cyan-400/80 shrink-0 mt-0.5" />
                <span>Field calculators for private equity</span>
              </li>
              <li className="flex items-start gap-3">
                <ArrowRight className="w-5 h-5 text-cyan-400/80 shrink-0 mt-0.5" />
                <span>Phase estimators & dynamic charting for asset managers and financial advisors</span>
              </li>
              <li className="flex items-start gap-3">
                <ArrowRight className="w-5 h-5 text-cyan-400/80 shrink-0 mt-0.5" />
                <span>Account manager tools like prospect funnels and sales pipelines</span>
              </li>
            </ul>
            <p>
              Our mission is to provide you with custom-built software that acts as a foundational asset to your company, empowering you to operate without compromise.
            </p>
            <p className="text-white/85 font-medium italic">
              Build it once, own it forever, and leverage technology that works exactly the way you do.
            </p>
          </motion.div>
        </motion.section>

        <Divider />

        {/* ── Section 4: What We Build ─────────────────────────────────────── */}
        <motion.section
          className="mb-24"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
        >
          <motion.div variants={fadeUp} custom={0}>
            <SectionLabel>What We Build</SectionLabel>
          </motion.div>

          <motion.h2
            variants={fadeUp}
            custom={0.05}
            className="text-3xl font-bold leading-tight mb-10 text-white"
          >
            Systems engineered for your specific operations.
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {WORK_TYPES.map(({ label, descriptor }, i) => (
              <motion.div
                key={label}
                variants={fadeUp}
                custom={0.05 + i * 0.07}
                onClick={onSeePortfolio}
                className="group relative rounded-2xl p-6 border border-white/10 bg-white/[0.04] backdrop-blur-xl hover:border-cyan-400/30 hover:bg-white/[0.07] transition-all duration-300 flex flex-col cursor-pointer"
              >
                {/* Subtle top highlight */}
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent rounded-t-2xl" />

                <p className="text-[11px] tracking-[0.2em] uppercase text-cyan-400/60 mb-3 font-medium">
                  0{i + 1}
                </p>
                <h3 className="text-white font-semibold text-[15px] leading-snug mb-3">
                  {label}
                </h3>
                <p className="text-white/45 text-[13px] leading-relaxed flex-grow">
                  {descriptor}
                </p>

                {/* Portfolio CTA */}
                <div className="mt-5 flex items-center gap-1.5 text-[12px] text-white/30 group-hover:text-cyan-300 transition-colors">
                  <span>View in Portfolio</span>
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <Divider />

        {/* ── Section 5: The Team ──────────────────────────────────────────── */}
        <motion.section
          className="mb-24"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
        >
          <motion.div variants={fadeUp} custom={0}>
            <SectionLabel>The Team</SectionLabel>
          </motion.div>

          <motion.h2
            variants={fadeUp}
            custom={0.05}
            className="text-3xl font-bold leading-tight mb-8 text-white"
          >
            Led by Builders. Guided by Ownership.
          </motion.h2>

          <motion.div
            variants={fadeUp}
            custom={0.1}
            className="text-base text-white/65 leading-relaxed space-y-4 max-w-2xl"
          >
            <p>
              Tower was founded on a conviction: the best software is purpose-built for the company that uses it — not rented, not shared, not amortized across thousands of strangers. Our team consists of senior software engineers, system architects, and integration specialists who thrive on complex, multi-system problems.
            </p>
            <p className="text-white/85 font-medium">
              Custom software. Full ownership. Zero compromise.
            </p>
          </motion.div>
        </motion.section>

        <Divider />

        {/* ── Section 6: CTA ───────────────────────────────────────────────── */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
        >
          <motion.div
            variants={fadeUp}
            custom={0}
            className="relative rounded-3xl p-10 sm:p-14 border border-white/10 bg-white/[0.04] backdrop-blur-xl overflow-hidden text-center"
          >
            {/* Glow accent */}
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />

            <p className="text-[11px] tracking-[0.25em] uppercase text-cyan-400/60 font-medium mb-4">
              Get Started
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
              Ready to Own Your Competitive Edge?
            </h2>
            <p className="text-white/50 text-base leading-relaxed mb-10 max-w-lg mx-auto">
              Stop renting software that wasn't built for you. Let's architect a system designed around your exact operations — one you'll own outright.
            </p>

            <button
              onClick={onContact}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-sm tracking-wide text-[#011d33] bg-gradient-to-r from-cyan-300 to-blue-400 hover:from-cyan-200 hover:to-blue-300 transition-all duration-200 shadow-[0_0_40px_rgba(34,211,238,0.25)] hover:shadow-[0_0_60px_rgba(34,211,238,0.4)] hover:scale-[1.02] active:scale-[0.98]"
            >
              Schedule a Discovery Call
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </motion.section>

        {/* ── Footer ───────────────────────────────────────────────────────── */}
        <DevPlaque />

      </div>
    </div>
  );
};

export default AboutPage;
